

import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { TagModule } from 'primeng/tag';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { Subscription } from 'rxjs';
import { AppErrorMessagesComponent } from 'src/app/base/error-messages/app-error-messages.component';
import {
  ClientDTO, CreateUserDTO, FilterCondition, FilterDTO,
  FilterOperator, Role, SortDirection, SubscriptionDTO, UpdateUserInfoDTO, UserInfoDTO
} from 'src/app/base/models/models.swagger.generated';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { SearchFieldComponent } from 'src/app/base/shared/search-field/search-field.component';
import { FilterMap } from 'src/app/base/shared/table/filter-map';
import { PagedQueryModel } from 'src/app/base/shared/table/paged-query-model';
import { UserEditFormComponent } from '../user-edit-form/user-edit-form.component';
import { UserEditFormType } from '../user-edit-form/user-edit-form-type';
import { UserManagementService } from './user-management.service';
import { ClientService } from '../services/client/client.service';
import { AuthService } from '../services/auth.service';
import { SubscriptionService } from 'src/app/domain/master-data/subscription/subscription.service';
import { ToastService } from 'src/app/base/services/toast.service';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
  standalone: true,
  imports: [
    TableModule, ButtonModule, ToolbarModule, ToastModule, DialogModule,
    ReactiveFormsModule, TranslateModule, AppErrorMessagesComponent,
    SearchFieldComponent, UserEditFormComponent, TagModule, CommonModule, FormsModule
  ]
})
export class UserManagementComponent implements OnInit, OnDestroy {
  public data: UserInfoDTO[] = [];
  public totalNoOfRecords: number = 0;
  public selectedUser: UserInfoDTO;
  public itemDialog: boolean = false;
  public pagedQuery: PagedQueryModel = PagedQueryModel.getDefault();
  public form: FormGroup<UserEditFormType>;
  public globalFilters = ['firstname', 'lastname', 'email', 'client.name'];
  public columnFilters: { [key: string]: any } = {};
  private _subscription: Subscription = new Subscription();
  private searchText: string = '';
  public isCreateMode = false;
  public subscriptions: SubscriptionDTO[] = [];


  public originalRoles: Role[] = [];

  public RoleOptions = [
    { label: 'None', value: Role.None },
    { label: 'Admin', value: Role.Admin },
    { label: 'Client Admin', value: Role.ClientAdmin },
    { label: 'Web User', value: Role.WebUser },
    { label: 'Mobile User', value: Role.MobileUser },
    { label: 'Rest Service', value: Role.RestService },
    { label: 'Delivery Executive', value: Role.DeliveryExecutive },
  ];

  public getRoleLabels(roleIds: number[]): string {
    if (!roleIds || roleIds.length === 0) return '';
    return roleIds
      .map(roleId => this.RoleOptions.find(option => option.value === roleId)?.label || roleId.toString())
      .join(', ');
  }

  constructor(
    private userManagementService: UserManagementService,
    private modalService: ModalService,
    private fb: FormBuilder,
    private clientService: ClientService,
    public authService: AuthService,
    private subscriptionService: SubscriptionService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    const user = this.authService.getUserInfo();
    console.log('User roles:', user?.roles);
    console.log('isUserAdmin:', this.authService.isUserAdmin());
    console.log('isUserClientAdmin:', this.authService.isUserClientAdmin());
    this.loadData();

    if (this.authService.isUserAdmin()) {
      this.loadSubscriptions();
    }
  }

  private loadSubscriptions() {
    const subscriptionQuery = new PagedQueryModel();
    subscriptionQuery.skip = 0;
    subscriptionQuery.take = 100;
    subscriptionQuery.sortBy = 'name';
    subscriptionQuery.sortDirection = SortDirection.Ascending;

    this._subscription.add(
      this.subscriptionService.getData(subscriptionQuery).subscribe((result) => {
        this.subscriptions = result.items as SubscriptionDTO[] || [];
      })
    );
  }

  public get filterMatchModeOptions() {
    return FilterMap.getStringFilterMatchModeOptions();
  }

  private loadData() {
    this._subscription.add(
      this.userManagementService.getData(this.pagedQuery).subscribe((data) => {
        this.data = data.items;
        this.totalNoOfRecords = data.totalNumberOfItems;
        this.selectedUser = null;
        this.itemDialog = false;
      })
    );
  }

  private getFilters(): FilterDTO[] {
    const filters: FilterDTO[] = [];
    if (this.searchText) {
      const complexFilter = new FilterDTO({
        isComplex: true,
        condition: FilterCondition.Or,
        predicates: [
          new FilterDTO({ field: 'firstname', operator: FilterOperator.Contains, value: this.searchText, isComplex: false }),
          new FilterDTO({ field: 'lastname', operator: FilterOperator.Contains, value: this.searchText, isComplex: false }),
          new FilterDTO({ field: 'email', operator: FilterOperator.Contains, value: this.searchText, isComplex: false }),
          new FilterDTO({ field: 'client.name', operator: FilterOperator.Contains, value: this.searchText, isComplex: false })
        ]
      });
      filters.push(complexFilter);
    }
    Object.entries(this.columnFilters).forEach(([key, filter]) => {
      if (Array.isArray(filter)) {
        const columnFilters = filter
          .filter(f => f.value)
          .map(f => new FilterDTO({
            field: key,
            operator: FilterMap.getFilterOperator(f.matchMode),
            value: f.value,
            isComplex: false
          }));
        if (columnFilters.length > 1) {
          filters.push(new FilterDTO({ isComplex: true, condition: FilterCondition.Or, predicates: columnFilters }));
        } else if (columnFilters.length === 1) {
          filters.push(columnFilters[0]);
        }
      } else if (filter?.value) {
        filters.push(new FilterDTO({
          field: key,
          operator: FilterMap.getFilterOperator(filter.matchMode),
          value: filter.value,
          isComplex: false
        }));
      }
    });
    return filters;
  }

  public get clients(): ClientDTO[] {
    return this.clientService.clients;
  }

  public onFilter(event: any) {
    this.columnFilters = event.filters;
    this.pagedQuery.filters = this.getFilters();
    this.loadData();
  }

  public openNew() {
    this.isCreateMode = true;
    this.selectedUser = null;
    this.originalRoles = [];
    this.initForm();
    this.itemDialog = true;
  }

  public editItem(item: UserInfoDTO) {
    this.isCreateMode = false;
    this.selectedUser = item;

    this.originalRoles = [...(item.roles ?? [])];
    this.initForm();
    this.itemDialog = true;
  }

  public hideDialog() {
    this.itemDialog = false;
  }

  public saveUser() {
    if (!this.form.valid) return;

    const payload: any = { ...this.form.value };

    if (!this.authService.isUserAdmin()) {
      delete payload.subscriptionId;
    }

    const userId = payload.userId?.trim?.() || null;

    if (userId) {
      this._subscription.add(
        this.userManagementService.updateRecord(payload as UpdateUserInfoDTO)
          .subscribe({
            next: (updatedUser: UserInfoDTO) => {
              this.selectedUser = updatedUser;

              if (this.authService.isUserAdmin() && this.form.controls.subscriptionId) {
                this.form.controls.subscriptionId.setValue(
                  updatedUser.activeSubscriptionId,
                  { emitEvent: false }
                );
              }

              this.loadData();
            },
            error: (error) => {

              const apiMessage = error?.error?.message as string | undefined;

              if (apiMessage) {
                this.toastService.showError(
                  'Unable to save',
                  apiMessage
                );
              }

            }
          })
      );
    } else {
      this._subscription.add(
        this.userManagementService.addRecord(payload as CreateUserDTO)
          .subscribe(() => {
            this.loadData();
          })
      );
    }
  }

  public getCurrentSubscriptionName(): string | undefined {
    if (this.selectedUser?.activeSubscriptionName) {
      return this.selectedUser.activeSubscriptionName;
    }

    const subscriptionId = this.form?.controls.subscriptionId?.value;
    if (subscriptionId) {
      const subscription = this.subscriptions.find(s => s.id === subscriptionId);
      return subscription?.name;
    }

    return undefined;
  }

  public onPageChange(event: any) {
    this.pagedQuery.skip = event.first;
    this.pagedQuery.take = event.rows;
    this.loadData();
  }

  public onSearchTextChanged(searchText: string) {
    this.searchText = searchText;
    this.pagedQuery.filters = this.getFilters();
    this.pagedQuery.skip = 0;
    this.loadData();
  }

  public onSort(event: any) {
    this.pagedQuery.sortBy = event.field;
    this.pagedQuery.sortDirection = event.order === 1 ? SortDirection.Ascending : SortDirection.Descending;
    this.loadData();
  }

  private initForm() {
    const existingRoles = this.selectedUser?.roles ?? [];
    const isAdmin = existingRoles.includes(Role.Admin);
    const clientIdValidators = isAdmin ? [] : [Validators.required];

    this.form = this.fb.group<UserEditFormType>({
      userId: this.fb.control(this.selectedUser?.userId ?? null),
      firstname: this.fb.control(this.selectedUser?.firstname ?? '', [Validators.required]),
      lastname: this.fb.control(this.selectedUser?.lastname ?? '', [Validators.required]),
      language: this.fb.control(this.selectedUser?.language ?? ''),
      email: this.fb.control(this.selectedUser?.email ?? '', [Validators.required, Validators.email]),
      phoneNumber: this.fb.control(this.selectedUser?.phoneNumber ?? '', [
        Validators.minLength(12),
        Validators.maxLength(12),
        Validators.pattern(/^\d{12}$/)
      ]),
      enabled: this.fb.control(this.selectedUser?.enabled ?? true),
      clientId: this.fb.control(this.selectedUser?.clientId ?? null, clientIdValidators),
      roles: this.fb.control(existingRoles, [Validators.required]),
      subscriptionId: this.fb.control(this.selectedUser?.activeSubscriptionId ?? null),
    });
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }
}